import React from 'react';
import {
  Box, Drawer, Typography, IconButton, Divider, Button, Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { brandColors, RADIUS, brandCss } from '../../theme/appTheme';
import { statusChip } from './SuperAdminDataTable';

export interface SuperAdminDetailField {
  label: string;
  value: React.ReactNode;
  fullWidth?: boolean;
}

interface SuperAdminDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  moduleTitle: string;
  title: string;
  subtitle?: string;
  status?: string;
  fields: SuperAdminDetailField[];
  footer?: React.ReactNode;
  primaryActions?: React.ReactNode;
  children?: React.ReactNode;
}

export const SuperAdminDetailDrawer: React.FC<SuperAdminDetailDrawerProps> = ({
  open,
  onClose,
  moduleTitle,
  title,
  subtitle,
  status,
  fields,
  footer,
  primaryActions,
  children,
}) => (
  <Drawer
    anchor="right"
    open={open}
    onClose={onClose}
    PaperProps={{
      sx: {
        width: { xs: '100%', sm: 420, md: 480 },
        borderTopLeftRadius: `${RADIUS}px`,
        borderBottomLeftRadius: `${RADIUS}px`,
      },
    }}
  >
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{
        px: 2.5,
        py: 2,
        background: `linear-gradient(135deg, ${brandCss.primaryDark} 0%, ${brandCss.primary} 100%)`,
        color: '#fff',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ opacity: 0.85, textTransform: 'uppercase', letterSpacing: 1 }}>
              {moduleTitle}
            </Typography>
            <Typography fontWeight={800} fontSize={18} sx={{ wordBreak: 'break-word' }}>{title}</Typography>
            {subtitle && (
              <Typography fontSize={12} sx={{ opacity: 0.9, mt: 0.5 }}>{subtitle}</Typography>
            )}
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#fff' }} size="small"><CloseIcon /></IconButton>
        </Box>
        {status && (
          <Box sx={{ mt: 1.5 }}>{statusChip(status)}</Box>
        )}
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2 }}>
        <Typography fontSize={11} fontWeight={800} color={brandColors.textMuted} letterSpacing={1} sx={{ mb: 1.5 }}>
          FULL INFORMATION
        </Typography>
        <Grid container spacing={2}>
          {fields.map((field) => (
            <Grid item xs={12} sm={field.fullWidth ? 12 : 6} key={field.label}>
              <Typography fontSize={11} fontWeight={700} color={brandColors.textMuted} sx={{ mb: 0.25 }}>
                {field.label}
              </Typography>
              <Typography fontSize={14} fontWeight={600} color={brandColors.textDark} sx={{ wordBreak: 'break-word' }}>
                {field.value ?? '—'}
              </Typography>
            </Grid>
          ))}
        </Grid>
        {children}
        {footer && (
          <>
            <Divider sx={{ my: 2 }} />
            {footer}
          </>
        )}
      </Box>

      <Box sx={{
        px: 2.5, py: 2, borderTop: `1px solid ${brandColors.border}`,
        display: 'flex', gap: 1, flexWrap: 'wrap', bgcolor: '#fafbfc',
      }}>
        {primaryActions}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600 }}>Close</Button>
      </Box>
    </Box>
  </Drawer>
);
